import { AfterViewChecked, ChangeDetectorRef, Directive, ElementRef, Input, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';

import { equals } from '@awp/core/utils';
import { ContentProvider } from '../content.provider';

@Directive({
  selector: '[content]:not(ap-content)'
})
export class ContentDirective implements AfterViewChecked, OnDestroy {

  private key: string;
  private lastParams: any;
  private currentParams: any;
  private onLangChangeSubscription: Subscription;

  @Input() set content(key: string) {
    if (key) {
      this.key = key;
      this.checkNodes();
    }
  }

  @Input() set contentParams(params: any) {
    if (!equals(this.currentParams, params)) {
      this.currentParams = params;
      this.checkNodes(true);
    }
  }

  constructor(
    private contentProvider: ContentProvider,
    private element: ElementRef,
    private changeDetectorRef: ChangeDetectorRef) {

    // subscribe to onLangChange event, in case the language changes
    this.onLangChangeSubscription = this.contentProvider.onLangChanged
      .subscribe(p => {
        this.checkNodes(true);
      });
  }

  ngAfterViewChecked() {
    this.checkNodes();
  }

  checkNodes(forceUpdate = false) {
    let nodes: NodeList = this.element.nativeElement.childNodes;
    // if the element is empty
    if (!nodes.length) {
      // we add the key as content
      this.setContent(this.element.nativeElement, this.key);
      nodes = this.element.nativeElement.childNodes;
    }
    for (let i = 0; i < nodes.length; ++i) {
      const node: any = nodes[i];
      if (node.nodeType === 3) { // node type 3 is a text node
        let key: string;
        if (this.key) {
          key = this.key;
          if (forceUpdate) {
            node.lastKey = null;
          }
        } else {
          const content = this.getContent(node);
          const trimmedContent = content.trim();
          if (trimmedContent.length) {
            // we want to use the content as a key, not the translation value
            if (content !== node.currentValue) {
              key = trimmedContent;
              // the content was changed from the user, we'll use it as a reference if needed
              node.originalContent = this.getContent(node);
            } else if (node.originalContent && forceUpdate) { // the content seems ok, but the lang has changed
              node.lastKey = null;
              // the current content is the translation, not the key, use the last real content as key
              key = node.originalContent.trim();
            }
          }
        }
        this.updateValue(key, node);
      }
    }
  }

  updateValue(key: string, node: any) {
    if (key) {

      if (node.lastKey === key && this.lastParams === this.currentParams) {
        return;
      }

      this.lastParams = this.currentParams;

      this.contentProvider.get(key, this.currentParams)
        .subscribe((res: string) => {
          if (res !== key) {
            node.lastKey = key;
          }
          if (!node.originalContent) {
            node.originalContent = this.getContent(node);
          }
          node.currentValue = res != null ? res : (node.originalContent || key);
          // we replace in the original content to preserve spaces that we might have trimmed
          this.setContent(node, this.key ? node.currentValue : node.originalContent.replace(key, node.currentValue));
          this.changeDetectorRef.markForCheck();
        });
    }
  }

  getContent(node: any): string {
    return node.textContent != null ? node.textContent : node.data;
  }

  setContent(node: any, content: string): void {
    if (node.textContent != null) {
      node.textContent = content;
    } else {
      node.data = content;
    }
  }

  ngOnDestroy() {
    this.onLangChangeSubscription.unsubscribe();
    this.onLangChangeSubscription = undefined;
  }
}
